import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import { Eye, Layers, Search, Waypoints } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Container, NetworkBackdrop } from '../components/ui'
import { usePageTitle } from '../hooks/usePageTitle'

/**
 * Landing surface — brand copy + search. CVR (8 digits) → /company/:cvr;
 * free-text → /search for companies and persons.
 */
const CVR_NUMBER_PATTERN = /^\d{8}$/

const INTEL_LAYERS = [
  {
    icon: Layers,
    title: 'Ejerskab',
    body: 'Reelle ejere, holdings og ejerskabsstrukturer — fremhævet og læsbart, så du hurtigt ser, hvem der trækker i trådene bag et selskab.',
  },
  {
    icon: Waypoints,
    title: 'Netværk',
    body: 'Følg forbindelser fra selskab til person og videre til meddeltagere. Se overlap og skjulte lag på tværs af strukturer.',
  },
  {
    icon: Eye,
    title: 'Overvågning',
    body: 'Gem selskaber og personer på watchlist. Få besked, når roller, ejerskab eller stamdata ændrer sig.',
  },
] as const

const HomePage: React.FC = () => {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  usePageTitle('Søg')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const q = query.trim()
    if (!q) return
    if (CVR_NUMBER_PATTERN.test(q)) {
      navigate(`/company/${q}`)
    } else {
      navigate(`/search?q=${encodeURIComponent(q)}`)
    }
  }

  return (
    <div className="bg-paper min-h-[calc(100vh-3.5rem)]">
      <Container width="prose" className="pt-16 pb-24">
        <div className="relative mb-12">
          <NetworkBackdrop className="hidden md:block absolute right-0 -top-[30px] w-[22.5rem] h-40" />
          <p className="font-mono text-xs uppercase tracking-widest text-signal mb-3">
            XOSA · INTELLIGENCE
          </p>
          <h1 className="relative font-serif text-4xl sm:text-5xl text-ink leading-tight">
            Hold øje med bagmændene.
          </h1>
          <p className="mt-4 text-sm text-ink-600 max-w-prose leading-relaxed">
            Se hvem, der står bag virksomheder. Se gennem deres netværk. Få
            notifikationer, når nogen gør noget.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mb-14">
          <label htmlFor="home-search" className="sr-only">
            Søg på CVR-nummer, virksomheder eller personer
          </label>
          <div className="flex border-2 border-ink rounded-sm overflow-hidden bg-paper focus-within:ring-2 focus-within:ring-signal focus-within:border-ink">
            <input
              id="home-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søg på CVR-nummer, virksomheder eller personer"
              autoFocus
              className="flex-1 px-4 py-3 text-base bg-transparent focus:outline-none placeholder:text-ink-400"
            />
            <button
              type="submit"
              disabled={!query.trim()}
              className="px-5 sm:px-6 bg-ink text-paper hover:bg-ink-700 disabled:bg-ink-300 inline-flex items-center gap-2 transition-colors"
            >
              <Search className="h-5 w-5" aria-hidden />
              <span className="hidden sm:inline">Slå op</span>
            </button>
          </div>
        </form>

        <section aria-label="Ejerskab, netværk og overvågning">
          <ul className="grid gap-3 sm:grid-cols-3">
            {INTEL_LAYERS.map(({ icon, title, body }) => (
              <IntelLayerCard key={title} icon={icon} title={title} body={body} />
            ))}
          </ul>
        </section>
      </Container>
    </div>
  )
}

interface IntelLayerCardProps {
  icon: LucideIcon
  title: string
  body: string
}

const IntelLayerCard: React.FC<IntelLayerCardProps> = ({ icon: Icon, title, body }) => {
  return (
    <li className="border border-rule rounded-sm p-5 bg-paper transition-colors hover:border-ink">
      <div className="flex items-center gap-3 mb-2">
        <Icon
          className="h-8 w-8 shrink-0 text-signal"
          strokeWidth={1.25}
          aria-hidden
        />
        <h3 className="font-serif text-base text-ink">{title}</h3>
      </div>
      <p className="text-sm text-ink-700 leading-relaxed">{body}</p>
    </li>
  )
}

export default HomePage
